/**
 * SuggestionContainer Component
 *
 * Container for Hero Search dropdown suggestions
 * Figma: Suggestion Container (Node: 595:4648)
 *
 * Structure:
 * - Gender Selection: Row of gender buttons
 * - Header: "Popular Searches" label
 * - Container: List of search suggestions
 * - Divider + Explore Brands section
 *
 * Spacing (exact Figma specs):
 * - Gap between sections: 32px
 * - Header height: 15px
 * - Divider section height: 83.5px with 33px top padding
 *
 * Typography:
 * - Header: 10px, regular, 1.1172px tracking, uppercase, #99a1af
 * - Suggestions: Handled by SearchSuggestion component
 */
"use client";

import { GenderLink } from "./GenderLink";
import {
  SEARCH_SUGGESTION_CATEGORY_OPTIONS,
  type SearchSuggestionCategory,
} from "./search-suggestion-config";
import { SearchSuggestion } from "./SearchSuggestion";

interface SuggestionContainerProps {
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
  onExploreBrandsClick: () => void;
  selectedGender?: SearchSuggestionCategory;
  onGenderSelect: (gender: SearchSuggestionCategory) => void;
}

export function SuggestionContainer({
  suggestions,
  onSuggestionClick,
  onExploreBrandsClick,
  selectedGender = "Women's",
  onGenderSelect,
}: SuggestionContainerProps) {
  return (
    <div
      className="flex w-full flex-col items-start"
      style={{
        gap: "32px",
      }}
    >
      {/* Gender Selection Section */}
      <div className="flex w-full flex-col items-start" style={{ gap: "17px" }}>
        {/* Category Header */}
        <div className="flex w-full items-center">
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: "10px",
              fontWeight: 400,
              lineHeight: "15px",
              letterSpacing: "1.1172px",
              textTransform: "uppercase",
              color: "#99a1af",
              whiteSpace: "pre",
            }}
          >
            Category
          </p>
        </div>

        {/* Gender Links */}
        <div className="flex w-full flex-wrap items-center gap-x-[28px] gap-y-3">
          {SEARCH_SUGGESTION_CATEGORY_OPTIONS.map((gender) => (
            <GenderLink
              key={gender}
              text={gender}
              isSelected={selectedGender === gender}
              onClick={() => onGenderSelect(gender)}
            />
          ))}
        </div>
      </div>

      {/* Header - "Popular Searches" (Figma Node: 595:4649) */}
      <div
        style={{
          height: "15px",
          width: "100%",
        }}
      >
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: "10px",
            fontWeight: 400,
            lineHeight: "15px",
            letterSpacing: "1.1172px",
            textTransform: "uppercase",
            color: "#99a1af", // Text/Header 4* - Gray Extra Lightest
            whiteSpace: "pre",
          }}
        >
          Popular Searches
        </p>
      </div>

      {/* Container for search suggestions (Figma Node: 595:4651) */}
      <div className="flex w-full flex-col items-start">
        {suggestions.map((suggestion, idx) => (
          <SearchSuggestion
            key={idx}
            text={suggestion}
            onClick={() => onSuggestionClick(suggestion)}
          />
        ))}
      </div>

      {/* Divider and Explore Brands section (Figma Node: 595:4673) */}
      <div
        className="flex w-full flex-col items-start"
        style={{
          height: "83.5px",
          borderTop: "1px solid rgba(0, 0, 0, 0.1)", // var(--divider/dividers-1*)
          paddingTop: "33px",
        }}
      >
        <SearchSuggestion text="Explore Brands" onClick={onExploreBrandsClick} showArrow={true} />
      </div>
    </div>
  );
}
